import {
    AfterViewInit,
    Directive,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    Renderer2,
} from '@angular/core';

import { QWYK_TABLE } from './table.component';

@Directive({
    standalone: true,
    selector: '[qwykResizableColumn]',
})
export class QwykResizableColumnDirective implements AfterViewInit, OnDestroy {
    /** Column identifier used for state persistence */
    @Input('qwykResizableColumn') columnId: string;

    private readonly _el = inject(ElementRef<HTMLElement>);
    private readonly _renderer = inject(Renderer2);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _table = inject<any>(QWYK_TABLE, { optional: true });

    private _handle: HTMLElement;
    private _cleanupListeners: (() => void)[] = [];

    ngAfterViewInit(): void {
        // Tag this th so siblings can be batch-saved after resize
        if (this.columnId) {
            this._renderer.setAttribute(this._el.nativeElement, 'data-qwyk-col-id', this.columnId);
        }

        // Restore saved width for this column
        if (this._table && this.columnId) {
            const saved = this._table._getColumnWidth(this.columnId);
            if (saved) {
                this._renderer.setStyle(this._el.nativeElement, 'width', `${saved}px`);
            }
        }

        this._handle = this._renderer.createElement('span');
        this._renderer.addClass(this._handle, 'qwyk-col-resizer');
        this._renderer.appendChild(this._el.nativeElement, this._handle);

        this._cleanupListeners.push(
            this._renderer.listen(this._handle, 'mousedown', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation(); // don't trigger sort click
                this._startResize(e);
            })
        );
    }

    ngOnDestroy(): void {
        this._cleanupListeners.forEach(fn => fn());
        this._cleanupListeners = [];
    }

    private _startResize(startEvent: MouseEvent): void {
        const th = this._el.nativeElement as HTMLElement;
        const tr = th.closest('tr') as HTMLTableRowElement | null;
        const tableEl = th.closest('table') as HTMLElement | null;

        // Snapshot the current auto-calculated widths for ALL columns and the table.
        // Freeze at max(offsetWidth, scrollWidth) so the initial switch to fixed layout
        // never makes a column narrower than its header content.
        if (tableEl && !tableEl.style.width) {
            this._renderer.setStyle(tableEl, 'width', `${tableEl.offsetWidth}px`);
        }
        if (tr) {
            Array.from(tr.cells).forEach(cell => {
                if (!cell.style.width) {
                    const w = Math.max(cell.offsetWidth, cell.scrollWidth);
                    this._renderer.setStyle(cell, 'width', `${w}px`);
                }
            });
        }

        const startX = startEvent.pageX;
        const startWidth = th.offsetWidth;
        const startTableWidth = tableEl?.offsetWidth ?? 0;
        const containerWidth = tableEl?.parentElement?.offsetWidth ?? startTableWidth;

        const moveCleanup = this._renderer.listen('document', 'mousemove', (e: MouseEvent) => {
            const delta = e.pageX - startX;
            const newColWidth = Math.max(20, startWidth + delta);
            this._renderer.setStyle(th, 'width', `${newColWidth}px`);
            // Expand mode: grow the table so other columns keep their widths
            if (tableEl) {
                const newTableWidth = Math.max(containerWidth, startTableWidth + delta);
                this._renderer.setStyle(tableEl, 'width', `${newTableWidth}px`);
            }
        });

        const upCleanup = this._renderer.listen('document', 'mouseup', () => {
            moveCleanup();
            upCleanup();
            if (!this._table) return;
            // Persist ALL column widths (not just the dragged one) so the full
            // frozen layout is correctly restored after a page reload.
            const allWidths: Record<string, number> = {};
            if (tr) {
                Array.from(tr.cells).forEach(cell => {
                    const colId = cell.getAttribute('data-qwyk-col-id');
                    if (colId) allWidths[colId] = cell.offsetWidth;
                });
            }
            this._table._batchSaveColumnWidths(allWidths, tableEl?.offsetWidth ?? null);
        });
    }
}
